import { usePage } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"
import { Building2, Mail, User, UserPlus, Lock } from "lucide-react"

interface AcceptInvitationProps {
  invitation: {
    name: string
    email: string
    account_name: string
    token: string
  }
  fieldErrors?: Record<string, string>
  [key: string]: unknown
}

export default function AcceptInvitation() {
  const { t } = useTranslation()
  const { invitation, fieldErrors } = usePage<AcceptInvitationProps>().props
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (password !== confirmPassword) {
      e.preventDefault()
      setConfirmError(t("acceptInvitation.passwordsMustMatch"))
      return
    }
    setConfirmError(null)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <UserPlus className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t("acceptInvitation.title")}</CardTitle>
          <CardDescription>{t("acceptInvitation.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6 p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-muted-foreground" />
              <span className="font-semibold text-lg">{invitation.account_name}</span>
            </div>
          </div>

          <form method="post" action={`/invite/${invitation.token}/accept`} onSubmit={handleSubmit} className="space-y-4">
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <FormError error={fieldErrors?.base} className="text-sm text-destructive text-center" />

            <div className="space-y-2">
              <Label htmlFor="name">{t("acceptInvitation.nameLabel")}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  value={invitation.name}
                  disabled
                  className="pl-10 bg-muted"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("acceptInvitation.emailLabel")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={invitation.email}
                  disabled
                  className="pl-10 bg-muted"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("acceptInvitation.passwordLabel")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("acceptInvitation.passwordHint")}</p>
              <FormError error={fieldErrors?.password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-confirm">{t("acceptInvitation.confirmPasswordLabel")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password-confirm"
                  name="password-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setConfirmError(null)
                  }}
                  className="pl-10"
                  required
                  minLength={8}
                />
              </div>
              <FormError error={confirmError || fieldErrors?.["password-confirm"]} />
            </div>

            <Button type="submit" className="w-full">
              <UserPlus className="w-4 h-4 mr-2" />
              {t("acceptInvitation.acceptButton")}
            </Button>

            <p className="text-center text-sm text-muted-foreground pt-2">
              {t("acceptInvitation.hasAccount")}{" "}
              <a href={`/login?invitation_token=${invitation.token}`} className="text-foreground hover:underline">
                {t("acceptInvitation.signIn")}
              </a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
